import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import {
  $completeInventoryScaleTicket,
  $completePriceScaleTicket,
  $holdScaleTicket,
  $payScaleTicket,
  $releaseScaleTicket,
  $voidScaleTicket,
} from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';

type UseTicketActionsOptions = {
  ticketId: string | null | undefined;
};

export const useTicketActions = ({ ticketId }: UseTicketActionsOptions) => {
  const t = useTranslations();
  const toast = useToast();

  const [isProcessing, setIsProcessing] = React.useState(false);

  const holdMutation = useSetAtom($holdScaleTicket);
  const releaseMutation = useSetAtom($releaseScaleTicket);
  const completeInventoryMutation = useSetAtom($completeInventoryScaleTicket);
  const completePriceMutation = useSetAtom($completePriceScaleTicket);
  const payMutation = useSetAtom($payScaleTicket);
  const voidMutation = useSetAtom($voidScaleTicket);

  const handleHold = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await holdMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.detail.toast.holdSuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.holdError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, holdMutation, toast, t]);

  const handleRelease = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await releaseMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.detail.toast.releaseSuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.releaseError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, releaseMutation, toast, t]);

  const handleCompleteInventory = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await completeInventoryMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.detail.toast.completeInventorySuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.completeInventoryError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, completeInventoryMutation, toast, t]);

  const handleCompletePrice = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await completePriceMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.detail.toast.completePricingSuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.completePricingError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, completePriceMutation, toast, t]);

  const handlePay = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await payMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.detail.toast.paySuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.payError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, payMutation, toast, t]);

  const handleVoid = React.useCallback(
    async (voidReason?: string) => {
      if (!ticketId) return;
      try {
        setIsProcessing(true);
        await voidMutation({ id: ticketId, voidReason });
        toast.success(t.scaleTicketPro.detail.toast.voidSuccess);
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : t.scaleTicketPro.detail.toast.voidError,
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [ticketId, voidMutation, toast, t],
  );

  return {
    isProcessing,
    handleHold,
    handleRelease,
    handleCompleteInventory,
    handleCompletePrice,
    handlePay,
    handleVoid,
  };
};
